import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { SearchHistoryEntry } from '../types'

const STORAGE_KEY = 'tecdoc-search-history'
const MAX_ENTRIES = 15

function loadFromStorage(): SearchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as SearchHistoryEntry[]
    // Migrate legacy entries that only have regNr
    return parsed.map(e => ({
      ...e,
      type: e.type || 'regnr',
      query: e.query || e.regNr || '',
    }))
  } catch {
    return []
  }
}

function saveToStorage(entries: SearchHistoryEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // localStorage full or unavailable
  }
}

export const useSearchHistoryStore = defineStore('searchHistory', () => {
  const entries = ref<SearchHistoryEntry[]>(loadFromStorage())

  function addEntry(entry: Omit<SearchHistoryEntry, 'timestamp'>) {
    // Remove existing entry with same type + query
    entries.value = entries.value.filter(e =>
      !(e.type === entry.type && e.query === entry.query)
    )

    // Add at the front
    entries.value.unshift({
      ...entry,
      timestamp: Date.now(),
    })

    // Keep max entries
    if (entries.value.length > MAX_ENTRIES) {
      entries.value = entries.value.slice(0, MAX_ENTRIES)
    }

    saveToStorage(entries.value)
  }

  function removeEntry(type: string, query: string) {
    entries.value = entries.value.filter(e =>
      !(e.type === type && e.query === query)
    )
    saveToStorage(entries.value)
  }

  function clear() {
    entries.value = []
    saveToStorage(entries.value)
  }

  return {
    entries,
    addEntry,
    removeEntry,
    clear,
  }
})
